import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Masthead from "../components/Masthead";
import GameGrid from "../components/GameGrid";
import Keyboard from "../components/Keyboard";
import StatsModal from "../components/StatsModal";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, BarChart3, HelpCircle } from "lucide-react";
import { getPuzzleState, savePuzzleState, recordResult } from "../lib/stats";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

export default function Play() {
  const { id } = useParams();
  const [puzzle, setPuzzle] = useState(null);
  const [guesses, setGuesses] = useState([]); // {letters, results}
  const [current, setCurrent] = useState("");
  const [status, setStatus] = useState("playing"); // playing | won | lost
  const [answer, setAnswer] = useState(null);
  const [shake, setShake] = useState(false);
  const [revealRow, setRevealRow] = useState(null);
  const [statsOpen, setStatsOpen] = useState(false);
  const [howOpen, setHowOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Load puzzle
  useEffect(() => {
    let mounted = true;
    api.get(`/puzzles/${id}`).then((r) => {
      if (!mounted) return;
      setPuzzle(r.data);
      const saved = getPuzzleState(id);
      if (saved?.guesses) {
        setGuesses(saved.guesses);
        if (saved.status) setStatus(saved.status);
        if (saved.answer) setAnswer(saved.answer);
      }
    }).catch(() => {
      toast.error("Puzzle not found");
    });
    return () => { mounted = false; };
  }, [id]);

  const wordLength = puzzle?.word_length || 5;
  const maxGuesses = puzzle?.max_guesses || 6;

  // Compute letter states for keyboard
  const letterStates = useMemo(() => {
    const map = {};
    const priority = { correct: 3, present: 2, absent: 1 };
    for (const g of guesses) {
      for (let i = 0; i < g.letters.length; i++) {
        const ch = g.letters[i];
        const st = g.results[i];
        if (!map[ch] || priority[st] > priority[map[ch]]) {
          map[ch] = st;
        }
      }
    }
    return map;
  }, [guesses]);

  const submit = useCallback(async () => {
    if (submitting || status !== "playing" || !puzzle) return;
    if (current.length !== wordLength) {
      setShake(true);
      setTimeout(() => setShake(false), 450);
      toast.error(`Word must be ${wordLength} letters`);
      return;
    }
    setSubmitting(true);
    try {
      const attempt = guesses.length + 1;
      const r = await api.post(`/puzzles/${id}/guess`, { guess: current, attempt });
      const letters = current.split("");
      const results = r.data.result;
      const revealedAnswer = r.data.answer || null;
      const newGuesses = [...guesses, { letters, results }];
      const rowIdx = guesses.length;
      setGuesses(newGuesses);
      setRevealRow(rowIdx);
      setCurrent("");

      // After flip finishes, evaluate end state
      setTimeout(() => {
        setRevealRow(null);
        if (r.data.is_win) {
          setStatus("won");
          setAnswer(current.toUpperCase());
          savePuzzleState(id, { guesses: newGuesses, status: "won", answer: current.toUpperCase() });
          recordResult(id, { won: true, guessesUsed: newGuesses.length });
          setTimeout(() => setStatsOpen(true), 300);
        } else if (newGuesses.length >= maxGuesses) {
          setStatus("lost");
          setAnswer(revealedAnswer);
          savePuzzleState(id, { guesses: newGuesses, status: "lost", answer: revealedAnswer });
          recordResult(id, { won: false, guessesUsed: newGuesses.length });
          setTimeout(() => setStatsOpen(true), 300);
        } else {
          savePuzzleState(id, { guesses: newGuesses, status: "playing" });
        }
      }, wordLength * 180 + 400);
    } catch (e) {
      setShake(true);
      setTimeout(() => setShake(false), 450);
      toast.error(e?.response?.data?.detail || "Invalid guess");
    } finally {
      setSubmitting(false);
    }
  }, [current, guesses, id, maxGuesses, puzzle, status, submitting, wordLength]);

  const handleKey = useCallback((k) => {
    if (status !== "playing" || revealRow !== null) return;
    if (k === "ENTER") {
      submit();
    } else if (k === "BACK") {
      setCurrent((c) => c.slice(0, -1));
    } else if (/^[A-Z]$/.test(k)) {
      setCurrent((c) => (c.length < wordLength ? c + k : c));
    }
  }, [status, revealRow, submit, wordLength]);

  // Physical keyboard
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "Enter") {
        e.preventDefault();
        handleKey("ENTER");
      } else if (e.key === "Backspace") {
        handleKey("BACK");
      } else if (/^[a-zA-Z]$/.test(e.key)) {
        handleKey(e.key.toUpperCase());
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [handleKey]);

  const lastResult = status !== "playing" ? {
    won: status === "won",
    guessesUsed: guesses.length,
    maxGuesses,
    guesses,
    answer: answer || "—",
  } : null;

  const themeStyle = puzzle ? {
    "--sage": puzzle.correct_color || "#6B8E73",
    "--mustard": puzzle.present_color || "#D4AF37",
  } : {};

  return (
    <div className="paper-texture min-h-screen" style={themeStyle}>
      <Masthead />
      <main className="relative z-10 max-w-3xl mx-auto px-6 pb-32">
        <div className="flex items-center justify-between mb-4">
          <Link to="/" className="font-mono-plex text-xs uppercase tracking-widest inline-flex items-center gap-1 hover:underline" data-testid="back-to-home">
            <ArrowLeft size={14} /> All Puzzles
          </Link>
          <div className="flex gap-2">
            <button className="btn-editorial ghost flex items-center gap-1.5 !py-2 !px-3" onClick={() => setHowOpen(true)} data-testid="how-btn">
              <HelpCircle size={14} /> How
            </button>
            <button className="btn-editorial ghost flex items-center gap-1.5 !py-2 !px-3" onClick={() => setStatsOpen(true)} data-testid="stats-btn">
              <BarChart3 size={14} /> Stats
            </button>
          </div>
        </div>

        {!puzzle ? (
          <p className="font-mono-plex text-sm text-center mt-16" style={{ color: "var(--muted)" }} data-testid="loading">Loading puzzle…</p>
        ) : (
          <>
            <div className="text-center mb-6">
              <div className="text-[11px] font-mono-plex uppercase tracking-[0.3em]" style={{ color: "var(--muted)" }}>
                Puzzle · {puzzle.word_length} letters · {puzzle.max_guesses} guesses
              </div>
              <h2 className="font-serif-display text-3xl sm:text-4xl mt-2" data-testid="puzzle-title">{puzzle.title}</h2>
              {puzzle.byline && (
                <p className="mt-1 font-mono-plex text-[11px] uppercase tracking-widest" style={{ color: "var(--muted)" }} data-testid="puzzle-byline">
                  By {puzzle.byline}
                </p>
              )}
              {puzzle.hint && (
                <p className="mt-2 italic text-sm" style={{ color: "var(--muted)" }} data-testid="puzzle-hint">
                  Hint: {puzzle.hint}
                </p>
              )}
              <div className="rule-thin mt-4" />
            </div>

            <div className="mb-8">
              <GameGrid
                wordLength={wordLength}
                maxGuesses={maxGuesses}
                guesses={guesses}
                current={current}
                shakeRow={shake}
                revealRow={revealRow}
              />
            </div>

            {status !== "playing" && (
              <div className="text-center mb-6" data-testid="game-status">
                <p className="font-serif-display text-2xl">
                  {status === "won" ? "Extra! Extra!" : "The presses stopped."}
                </p>
                <p className="text-sm mt-1" style={{ color: "var(--muted)" }}>
                  {status === "won"
                    ? `Solved in ${guesses.length}.`
                    : "Better luck next issue."}
                </p>
                {status === "lost" && answer && (
                  <div className="mt-4 inline-block border-2 border-[color:var(--ink-navy)] bg-white px-6 py-3" data-testid="revealed-answer">
                    <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em]" style={{ color: "var(--muted)" }}>
                      The Answer Was
                    </div>
                    <div className="font-serif-display text-3xl tracking-widest mt-1">{answer}</div>
                  </div>
                )}
              </div>
            )}

            <Keyboard onKey={handleKey} letterStates={letterStates} disabled={status !== "playing" || revealRow !== null} />
          </>
        )}
      </main>

      <StatsModal
        open={statsOpen}
        onClose={() => setStatsOpen(false)}
        lastResult={lastResult}
        puzzleTitle={puzzle?.title}
      />

      <Dialog open={howOpen} onOpenChange={setHowOpen}>
        <DialogContent className="max-w-md p-0 border-0 rounded-none bg-transparent shadow-none" data-testid="how-modal">
          <div className="rule-thick" />
          <div className="bg-white border-l-2 border-r-2 border-b-2 border-[color:var(--ink-navy)] p-6">
            <DialogHeader>
              <DialogTitle className="font-serif-display text-3xl">How to Brush Up</DialogTitle>
            </DialogHeader>
            <div className="mt-4 text-sm space-y-3">
              <p>Guess the word in a limited number of tries. Each guess must be a real English word of the right length.</p>
              <div className="grid grid-cols-5 gap-1">
                {["S","A","G","E","S"].map((c, i) => (
                  <div key={i} className={`tile ${i===0?"correct":i===2?"present":"absent"}`} style={{fontSize:"1.2rem"}}>{c}</div>
                ))}
              </div>
              <ul className="list-disc pl-5 space-y-1" style={{color: "var(--muted)"}}>
                <li><b>Green</b> — letter is in the correct spot.</li>
                <li><b>Gold</b> — letter is in the word, wrong spot.</li>
                <li><b>Slate</b> — letter isn't in the word.</li>
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
